"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getOptimisedCourseRate = void 0;
/* eslint-disable @typescript-eslint/no-explicit-any */
const _ = require("lodash");
const helpers_1 = require("../../helpers");
const dayjs = require("dayjs");
const getRatesForDate_1 = require("./getRatesForDate");
const getPreviousYearMatchingDay_1 = require("../getPreviousYearMatchingDay");
const saveRateToGolfLine_1 = require("./saveRateToGolfLine");
// if something is optional then everying should be yes
function findPreviousMatchingDay(givenDateStr, targetDateStr) {
    // Parse the input dates using Day.js
    const givenDate = dayjs(givenDateStr);
    const targetDate = dayjs(targetDateStr);
    // Get the day of the week for the target date (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const targetDayOfWeek = targetDate.day();
    // Copy the given date so we can modify it without affecting the original date
    let currentDate = givenDate;
    // Iterate backwards until we find the matching day of the week
    while (currentDate.isAfter(dayjs("1900-01-01"))) {
        if (currentDate.day() === targetDayOfWeek) {
            return currentDate.format("YYYY-MM-DD");
        }
        // Move to the previous day
        currentDate = currentDate.subtract(1, "day");
    }
    return null;
}
// if something is optional then everying should be yes
const getOptimisedCourseRate = ({ country_id, region_id, course_rates, course, date_of_play, number_of_golfers, course_closures, public_holiday, tee_time, carts, caddies, margin_discount_percentage = 0, default_historic_price_increase = 5, }) => {
    let historic_price_increase = 0;
    let historic_year_offset = 0;
    let golf_line_config = _.cloneDeep(helpers_1.GOLF_LINE_INITIAL_STATE);
    golf_line_config.where_when_who.date =
        dayjs(date_of_play).format("YYYY-MM-DD");
    golf_line_config.where_when_who.region_id = course.region_id;
    golf_line_config.where_when_who.number_of_golfers = number_of_golfers;
    golf_line_config.course = {
        id: course.id,
        name: course.name,
        policies: course.policies,
        hole_options: [],
        closures: course.course_closures,
        public_holidays: [],
        warnings: course.course_warnings,
    };
    // golf_line_config.hole_options = course.hole_options
    let rate_message = "";
    // based on all the rates given, get the rates for the current window.
    let available_rates = null;
    // get the rates for the current date selected
    const golf_rates = (0, getRatesForDate_1.getRatesForDate)(date_of_play, course_rates, course_closures, number_of_golfers, public_holiday);
    const has_active_contract = course_rates.filter((rate) => {
        const endDate = new Date(rate.end_date).setUTCHours(0, 0, 0, 0);
        const startDate = new Date(rate.start_date).setUTCHours(0, 0, 0, 0);
        const currentDate = date_of_play
            ? dayjs(date_of_play, "YYYY-MM-DD").toDate().setUTCHours(0, 0, 0, 0)
            : new Date().setUTCHours(0, 0, 0, 0);
        return currentDate >= startDate && currentDate <= endDate;
    }).length > 0;
    // if no rates found for current date, try to go back to the same day one year previously
    if (golf_rates.rates.length === 0 &&
        !golf_rates.closure &&
        !has_active_contract) {
        // get the rates base on the last date of the current rates
        historic_year_offset = 1;
        const sorted_rates = course_rates.length > 0
            ? course_rates.sort((a, b) => {
                return dayjs(b.end_date).diff(dayjs(a.end_date));
            })
            : [];
        const last_rate_entry = sorted_rates.length > 0 ? sorted_rates[0] : null;
        const last_rate_date = last_rate_entry
            ? findPreviousMatchingDay(last_rate_entry.end_date, date_of_play)
            : null;
        // get the closest date before this last_rate_date that matches the day of the week of the date_of_play
        const last_golf_rates = last_rate_date
            ? (0, getRatesForDate_1.getRatesForDate)(last_rate_date, course_rates, [], // ignore closures for previous years as we are only looking for rates
            number_of_golfers, public_holiday)
            : null;
        const prev_year_date_of_play = (0, getPreviousYearMatchingDay_1.getPreviousYearMatchingDay)(date_of_play, historic_year_offset);
        const prev_year_golf_rates = (0, getRatesForDate_1.getRatesForDate)(prev_year_date_of_play, course_rates, [], // ignore closures for previous years as we are only looking for rates
        number_of_golfers, public_holiday);
        historic_price_increase = default_historic_price_increase;
        if (prev_year_golf_rates.rates.length > 0) {
            rate_message = prev_year_golf_rates.message;
            // if there are last_golf_rates, iterate through each prev_year_golf_rates and if the product_id matches a product_id in last_golf_rates, then get the single_cart_price, shared_cart_price and caddie_price from last_golf_rates and update the prev_year_golf_rates with these values
            // if there are no last_golf_rates, then just return the prev_year_golf_rates
            prev_year_golf_rates.rates = prev_year_golf_rates.rates.map((prev_year_rate) => {
                const matching_rate = last_golf_rates?.rates?.find((last_rate) => last_rate.product_id === prev_year_rate.product_id);
                if (matching_rate) {
                    return {
                        ...prev_year_rate,
                        single_cart_price: matching_rate.single_cart_price,
                        shared_cart_price: matching_rate.shared_cart_price,
                        caddie_price: matching_rate.caddie_price,
                    };
                }
                return prev_year_rate;
            });
            available_rates = {
                historic: true,
                rates: prev_year_golf_rates.rates,
            };
        }
        // if no rates found for the previous year date, go yet another year back
        if (prev_year_golf_rates.rates.length === 0) {
            historic_year_offset = 2;
            const prev_year_date_of_play = (0, getPreviousYearMatchingDay_1.getPreviousYearMatchingDay)(date_of_play, historic_year_offset);
            const prev_year_golf_rates = (0, getRatesForDate_1.getRatesForDate)(prev_year_date_of_play, course_rates, [], // ignore closures for previous years as we are only looking for rates
            number_of_golfers, public_holiday);
            historic_price_increase = default_historic_price_increase * 2;
            if (prev_year_golf_rates.rates.length > 0) {
                rate_message = prev_year_golf_rates.message;
                prev_year_golf_rates.rates = prev_year_golf_rates.rates.map((prev_year_rate) => {
                    const matching_rate = last_golf_rates?.rates.find((last_rate) => last_rate.product_id === prev_year_rate.product_id);
                    if (matching_rate) {
                        return {
                            ...prev_year_rate,
                            single_cart_price: matching_rate.single_cart_price,
                            shared_cart_price: matching_rate.shared_cart_price,
                            caddie_price: matching_rate.caddie_price,
                        };
                    }
                    return prev_year_rate;
                });
                available_rates = {
                    historic: true,
                    rates: prev_year_golf_rates.rates,
                };
            }
        }
    }
    else {
        rate_message = golf_rates.message;
        available_rates = {
            historic: false,
            rates: golf_rates.rates,
        };
    }
    // if attempts to find a rate for the current date and the previous year date have failed, return an empty object
    if (!available_rates || available_rates.rates.length === 0) {
        return {
            config: golf_line_config,
            rate_message: golf_rates.message,
        };
    }
    // if more than one rate is found we need to do some filtering to get the single rate...  This will be using settings above..x
    if (available_rates.rates.length > 1) {
        // Filter for ONLY 18 hole rates
        // are there 9 hole rates but no 18 hole rates?
        const nine_hole_rates = available_rates.rates.filter((rate) => rate.product_config.holes === "9");
        const eighteen_hole_rates = available_rates.rates.filter((rate) => rate.product_config.holes === "18");
        if (eighteen_hole_rates.length === 0 && nine_hole_rates.length > 0) {
            available_rates.rates = nine_hole_rates;
        }
        else {
            available_rates.rates = eighteen_hole_rates;
        }
    }
    // filter rates by tee time range
    if (available_rates.rates.length > 1) {
        available_rates.rates = available_rates.rates.map((rate) => {
            const is_before = dayjs(rate.latest_time, "HH:mm").isBefore(dayjs(tee_time.start_time, "HH:mm"));
            const is_after = dayjs(rate.earliest_time, "HH:mm").isAfter(dayjs(tee_time.end_time, "HH:mm"));
            let distance_in_minutes = 0;
            if (is_before && !is_after) {
                distance_in_minutes = dayjs(tee_time.start_time, "HH:mm").diff(dayjs(rate.latest_time, "HH:mm"), "minutes");
            }
            if (is_after && !is_before) {
                distance_in_minutes = dayjs(rate.earliest_time, "HH:mm").diff(dayjs(tee_time.end_time, "HH:mm"), "minutes");
            }
            return {
                ...rate,
                distance_in_minutes,
            };
        });
        // if there are rates that are in the distance_in_minutes range, then i want to only use these rates
        const rates_in_range = available_rates.rates.filter((rate) => rate.distance_in_minutes === 0);
        if (rates_in_range.length > 0) {
            available_rates.rates = rates_in_range;
        }
    }
    // final step....get the total price (including caddies and carts) for the rate then sort by distance_in_minutes then cheapest rate is the one we want to use
    /*
        available_rates.rates = available_rates.rates.sort((a, b) => {
        if (a.distance_in_minutes === b.distance_in_minutes) {
          return a.earliest_time.localeCompare(b.earliest_time)
        }
        return (a?.distance_in_minutes ?? 0) - (b?.distance_in_minutes ?? 0)
      })
  
      // update the available rates to get the first one only in the array
      available_rates.rates = available_rates.rates.slice(0, 1)
    */
    available_rates.rates = available_rates.rates.map((rate) => {
        let caddie_price = 0;
        let cart_price = 0;
        if (!rate.product_config.caddie &&
            !rate.product_config.shared_caddie &&
            caddies) {
            caddie_price = Number(rate.caddie_price);
        }
        if (!rate.product_config.single_cart &&
            !rate.product_config.shared_cart &&
            carts) {
            cart_price = Number(rate.single_cart_price);
        }
        return {
            ...rate,
            blended_price: rate.sell_price + caddie_price + cart_price,
        };
    });
    if (available_rates.rates.length > 0) {
        // get the rate that has the cheapest blended price
        const rate = available_rates.rates.reduce((prev, current) => {
            if (prev.blended_price === current.blended_price) {
                return dayjs(prev.earliest_time, "HH:mm").isBefore(dayjs(current.earliest_time, "HH:mm"))
                    ? prev
                    : current;
            }
            return prev.blended_price < current.blended_price ? prev : current;
        });
        golf_line_config = (0, saveRateToGolfLine_1.saveRateToGolfLine)(country_id, region_id, golf_line_config, rate, course.policies, carts, caddies, margin_discount_percentage, tee_time, available_rates.historic
            ? {
                date: (0, getPreviousYearMatchingDay_1.getPreviousYearMatchingDay)(date_of_play, historic_year_offset),
                rate,
                percentage: historic_price_increase,
            }
            : null);
        return {
            config: golf_line_config,
            rate_message: rate_message,
        };
    }
    return {
        config: golf_line_config,
        rate_message: rate_message,
    };
};
exports.getOptimisedCourseRate = getOptimisedCourseRate;
